// frontend/src/storage/indexeddb.ts
const DB_NAME = 'RiskGuardOfflineDB';
const DB_VERSION = 2;

export const STORE_PHOTOS = 'photos';
export const STORE_CRONOGRAMA = 'cronograma';
export const STORE_APRECIACOES = 'apreciacoes';

export class IndexedDBService {
    private db: IDBDatabase | null = null;
    private initPromise: Promise<void> | null = null;

    async init(): Promise<void> {
        if (this.db) return Promise.resolve();
        if (this.initPromise) return this.initPromise;

        this.initPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = (event) => {
                console.error('Erro ao abrir IndexedDB', event);
                reject('Erro ao abrir banco de dados local');
            };

            request.onsuccess = (event) => {
                this.db = (event.target as IDBOpenDBRequest).result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Tabela para fotos capturadas offline
                if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
                    const photoStore = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' });
                    photoStore.createIndex('maquinaId', 'maquinaId', { unique: false });
                    photoStore.createIndex('status', 'status', { unique: false });
                    photoStore.createIndex('dataHora', 'dataHora', { unique: false });
                }

                // Tabela para cache do cronograma/máquinas para exibição na tela
                if (!db.objectStoreNames.contains(STORE_CRONOGRAMA)) {
                    db.createObjectStore(STORE_CRONOGRAMA, { keyPath: 'id' });
                }

                // Tabela para cache das apreciações de risco para visualização offline
                if (!db.objectStoreNames.contains(STORE_APRECIACOES)) {
                    const apreciacaoStore = db.createObjectStore(STORE_APRECIACOES, { keyPath: 'id' });
                    apreciacaoStore.createIndex('projetoId', 'projetoId', { unique: false });
                    apreciacaoStore.createIndex('atualizadoEm', 'atualizadoEm', { unique: false });
                }
            };
        });

        return this.initPromise;
    }

    async getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
        await this.init();
        if (!this.db) throw new Error('DB não inicializado');
        const transaction = this.db.transaction(storeName, mode);
        return transaction.objectStore(storeName);
    }

    async getAll<T>(storeName: string): Promise<T[]> {
        const store = await this.getStore(storeName);
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result as T[]);
            request.onerror = () => reject(request.error);
        });
    }

    async get<T>(storeName: string, key: string | number): Promise<T | undefined> {
        const store = await this.getStore(storeName);
        return new Promise((resolve, reject) => {
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result as T);
            request.onerror = () => reject(request.error);
        });
    }

    async put<T>(storeName: string, item: T): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.put(item);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async delete(storeName: string, key: string | number): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.delete(key);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async clear(storeName: string): Promise<void> {
        const store = await this.getStore(storeName, 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
}

export const dbService = new IndexedDBService();
